import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import "./BottomSheet.css"

/* The phone's answer to a dock.

   On a phone the map is the floor and every panel the docks used to hold arrives as one of these:
   it covers the map, it does not move it, and exactly one is open at a time. That last part is
   deliberate — two half-height panels on a 390px screen is the desktop layout again, smaller.

   The map keeps rendering underneath rather than unmounting, so closing a sheet gives back the
   view it covered — the same reason the modal stack keeps covered modals mounted.

   Dismissal has three doors, because a phone user will reach for whichever one they know: the
   backdrop, the close button, and a downward drag. The drag is the one worth explaining. It is
   tracked on the header only, never on the body, or a sheet whose content scrolls would close
   every time somebody scrolled it back to the top. */

// Far enough that it cannot be a mis-touch, near enough that it does not need a whole swipe.
const DISMISS_TRAVEL_PX = 90
// A short, fast flick means the same thing as a long slow drag.
const DISMISS_FLICK_PX = 24
const DISMISS_FLICK_MS = 300

const BottomSheet = ({ open, onClose, title, subtitle, children, className = "" }) => {
  const { t } = useTranslation()
  const [dragY, setDragY] = useState(0)
  const dragRef = useRef(null)

  const close = useCallback(() => {
    setDragY(0)
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!open) return undefined
    const onKeyDown = (event) => {
      if (event.key === "Escape") close()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [open, close])

  // A sheet reopened after being dragged partway down must not come back holding that offset.
  useEffect(() => {
    if (!open) setDragY(0)
  }, [open])

  if (!open) return null

  const onHeaderTouchStart = (event) => {
    const touch = event.touches[0]
    if (!touch) return
    dragRef.current = { startY: touch.clientY, startedAt: Date.now() }
  }

  const onHeaderTouchMove = (event) => {
    const drag = dragRef.current
    const touch = event.touches[0]
    if (!drag || !touch) return
    // Downward only: dragging up would lift the sheet off the bottom of the screen and show the
    // page's ground through the gap.
    setDragY(Math.max(0, touch.clientY - drag.startY))
  }

  const onHeaderTouchEnd = () => {
    const drag = dragRef.current
    dragRef.current = null
    if (!drag) return
    const flicked = Date.now() - drag.startedAt < DISMISS_FLICK_MS && dragY > DISMISS_FLICK_PX
    if (dragY > DISMISS_TRAVEL_PX || flicked) close()
    else setDragY(0)
  }

  return (
    <div className="sheet-layer">
      <button
        type="button"
        className="sheet-backdrop"
        aria-label={t("sheet.close")}
        onClick={close}
      />
      <section
        className={`sheet ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={dragY ? { transform: `translateY(${dragY}px)`, transition: "none" } : undefined}
      >
        <header
          className="sheet-header"
          onTouchStart={onHeaderTouchStart}
          onTouchMove={onHeaderTouchMove}
          onTouchEnd={onHeaderTouchEnd}
          onTouchCancel={onHeaderTouchEnd}
        >
          <span className="sheet-grabber" aria-hidden="true" />
          <div className="sheet-titles">
            <h2 className="sheet-title">{title}</h2>
            {subtitle ? <p className="sheet-subtitle">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            className="sheet-close"
            onClick={close}
            aria-label={t("sheet.close")}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        </header>
        <div className="sheet-body">{children}</div>
      </section>
    </div>
  )
}

export default BottomSheet
